import React, { useState, useEffect } from 'react';
import { Modal, Button, List, Checkbox, Icon, Loader, Dropdown } from 'semantic-ui-react';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { BasicDialog } from '@src/views/components/modals/BasicDialog';
import { BasicDialogContainer } from '@src/views/components/modals/BasicDialogContainer';
import { SemanticUIColor, Size, Align, FlexAlign, FlexJustify } from '@src/types';
import { StyledContainer, Flexbox, FlexItem } from '@src/views/containers';

export interface SelectedItem {
  objectType: 'Project_Id' | 'Project_Version_Id' | 'Category_Id' | 'Sub_Category_Id' | 'Sub_Objective_Id' | 'Activity_Id' | 'Static_Project_Activity_Id';
  objectId: string;
  name: string;
}

interface Project {
  id: string;
  name: string;
  launchDateUtc: string;
}

interface ProjectVersion {
  id: string;
  name: string;
  description: string;
  isActiveVersion: boolean;
}

interface StaticProjectActivity {
  id: string;
  projectVersionId: string;
  name: string;
  activityId: string;
  activityName: string;
  categoryId: string;
  categoryName: string;
  categoryDescription: string;
  subCategoryId: string;
  subCategoryName: string;
  subCategoryDescription: string;
  subObjectiveId: string;
  subObjectiveName: string;
  subObjectiveDescription: string;
  startDate: string;
  endDate: string;
}

interface CategoryItem {
  id?: string;
  displayName?: string;
  description?: string;
}

interface AIObjectPickerProps {
  open: boolean;
  onClose: (selected: SelectedItem[]) => void;
}

type SearchObjectType = SelectedItem['objectType'];

const AIObjectPicker: React.FC<AIObjectPickerProps> = ({ open, onClose }) => {
  const { TableSelect } = useCentralCalls();
  const [ projects, setProjects ]                                       = useState<Project[]>( [] ),
        [ expandedProjects, setExpandedProjects ]                       = useState<Set<string>>(new Set()),
        [ projectVersions, setProjectVersions ]                         = useState<Record<string, ProjectVersion[]>>( {} ),
        [ projectActivities, setProjectActivities ]                     = useState<Record<string, StaticProjectActivity[]>>( {} ),
        [ expandedCategories, setExpandedCategories ]                   = useState<Set<string>>(new Set()),
        [ expandedSubCategories, setExpandedSubCategories ]             = useState<Set<string>>(new Set()),
        [ expandedSubObjectives, setExpandedSubObjectives ]             = useState<Set<string>>(new Set()),
        [ selected, setSelected ]                                       = useState<Map<string, { type: string; name: string }>>(new Map()),
        [ loadingProjects, setLoadingProjects ]                         = useState( false ),
        [ loadingVersions, setLoadingVersions ]                         = useState<Set<string>>(new Set()),
        [ expandedRoot, setExpandedRoot ]                               = useState( false ),
        [ objects, setObjects ]                                         = useState<CategoryItem[]>( [] ),
        [ expandedObjectsRoot, setExpandedObjectsRoot ]                 = useState( false ),
        [ loadingObjects, setLoadingObjects ]                           = useState( false ),
        [ objectSubCategories, setObjectSubCategories ]                 = useState<Record<string, CategoryItem[]>>( {} ),
        [ expandedObjectCategories, setExpandedObjectCategories ]       = useState<Set<string>>(new Set()),
        [ loadingObjectSubCategories, setLoadingObjectSubCategories ]   = useState<Set<string>>(new Set()),
        [ objectActivities, setObjectActivities ]                       = useState<Record<string, any[]>>( {} ),
        [ expandedObjectSubCategories, setExpandedObjectSubCategories ] = useState<Set<string>>(new Set()),
        [ loadingObjectActivities, setLoadingObjectActivities ]         = useState<Set<string>>(new Set()),
        [ filterType, setFilterType ]                                   = useState<string>( '' );

  const filterOptions = [
    { key: 'h-project', value: 'History - Project', text: 'History - Project' },
    { key: 'h-projectversions', value: 'History - ProjectVersions', text: 'History - ProjectVersions' },
    { key: 'h-category', value: 'History - Category', text: 'History - Category' },
    { key: 'h-subcategory', value: 'History - SubCategory', text: 'History - SubCategory' },
    { key: 'h-objective', value: 'History - Objective', text: 'History - Objective' },
    { key: 'h-activity', value: 'History - Activity', text: 'History - Activity' },
    { key: 'o-category', value: 'Object - Category', text: 'Object - Category' },
    { key: 'o-subcategory', value: 'Object - SubCategory', text: 'Object - SubCategory' },
    { key: 'o-activity', value: 'Object - Activity', text: 'Object - Activity' },
  ];

  useEffect(() => {
    if (open && projects.length === 0) {
      loadProjects();
    }
  }, [open]);

  useEffect(() => {
    if (open && objects.length === 0) {
      loadObjects();
    }
  }, [open]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const data = await TableSelect('Projects', {IsCorvalManaged:false});
      setProjects(data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadSubCategories = async (categoryId: string) => {
    setLoadingObjectSubCategories(prev => new Set(prev).add(categoryId));
    try {
      const data = await TableSelect('SubCategories', { categoryId });
      setObjectSubCategories(prev => ({ ...prev, [categoryId]: data || [] }));
    } catch (error) {
      console.error('Error loading subcategories for category', categoryId, error);
    } finally {
      setLoadingObjectSubCategories(prev => {
        const newSet = new Set(prev);
        newSet.delete(categoryId);
        return newSet;
      });
    }
  };

  const toggleExpandObjectCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedObjectCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
      if (!objectSubCategories[categoryId]) {
        loadSubCategories(categoryId);
      }
    }
    setExpandedObjectCategories(newExpanded);
  };

  const loadObjects = async () => {
    setLoadingObjects(true);
    try {
      const data = await TableSelect('Categories', {});
      setObjects(data || []);
    } catch (error) {
      console.error('Error loading objects (categories):', error);
    } finally {
      setLoadingObjects(false);
    }
  };

  const loadProjectVersions = async (projectId: string) => {
    setLoadingVersions(prev => new Set(prev).add(projectId));
    try {
      const data = await TableSelect('ProjectVersions', { projectId: projectId });
      console.log('projects',data);
      setProjectVersions(prev => ({ ...prev, [projectId]: data || [] }));

      for (const version of data || []) {
        const activities = await TableSelect('StaticProjectActivities', { projectVersionId: version.id });
        setProjectActivities(prev => ({ ...prev, [version.id]: activities || [] }));
      }
    } catch (error) {
      console.error('Error loading project versions:', error);
    } finally {
      setLoadingVersions(prev => {
        const newSet = new Set(prev);
        newSet.delete(projectId);
        return newSet;
      });
    }
  };

  const toggleExpand = (projectId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
      if (!projectVersions[projectId]) {
        loadProjectVersions(projectId);
      }
    }
    setExpandedProjects(newExpanded);
  };

  const toggleExpandCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleExpandSubCategory = (subCategoryId: string) => {
    const newExpanded = new Set(expandedSubCategories);
    if (newExpanded.has(subCategoryId)) {
      newExpanded.delete(subCategoryId);
    } else {
      newExpanded.add(subCategoryId);
    }
    setExpandedSubCategories(newExpanded);
  };

  const toggleExpandSubObjective = (subObjectiveId: string) => {
    const newExpanded = new Set(expandedSubObjectives);
    if (newExpanded.has(subObjectiveId)) {
      newExpanded.delete(subObjectiveId);
    } else {
      newExpanded.add(subObjectiveId);
    }
    setExpandedSubObjectives(newExpanded);
  };

  const toggleSelect = (id: string, type: SearchObjectType, name: string) => {
    const newSelected = new Map(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.set(id, { type, name });
    }
    setSelected(newSelected);
  };

  const loadActivities = async (subCategoryId: string) => {
    setLoadingObjectActivities(prev => new Set(prev).add(subCategoryId));
    try {
      const data = await TableSelect('Activities', { subCategoryId });
      setObjectActivities(prev => ({ ...prev, [subCategoryId]: data || [] }));
    } catch (error) {
      console.error('Error loading activities for subcategory', subCategoryId, error);
    } finally {
      setLoadingObjectActivities(prev => {
        const newSet = new Set(prev);
        newSet.delete(subCategoryId);
        return newSet;
      });
    }
  };

  const toggleExpandObjectSubCategory = (subCategoryId: string) => {
    const newExpanded = new Set(expandedObjectSubCategories);
    if (newExpanded.has(subCategoryId)) {
      newExpanded.delete(subCategoryId);
    } else {
      newExpanded.add(subCategoryId);
      if (!objectActivities[subCategoryId]) {
        loadActivities(subCategoryId);
      }
    }
    setExpandedObjectSubCategories(newExpanded);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  };

  const getCategories = (projectVersionId: string) => {
    const activities = projectActivities[projectVersionId] || [];
    const categories = activities.reduce((acc, act) => {
      if (!acc.some(c => c.id === act.categoryId)) {
        acc.push({ id: act.categoryId, name: act.categoryName, description: act.categoryDescription });
      }
      return acc;
    }, [] as { id: string; name: string; description: string }[]);
    return categories;
  };

  const getSubCategories = (projectVersionId: string, categoryId: string) => {
    const activities = projectActivities[projectVersionId] || [];
    const subCategories = activities.filter(act => act.categoryId === categoryId).reduce((acc, act) => {
      if (!acc.some(sc => sc.id === act.subCategoryId)) {
        acc.push({ id: act.subCategoryId, name: act.subCategoryName, description: act.subCategoryDescription });
      }
      return acc;
    }, [] as { id: string; name: string; description: string }[]);
    return subCategories;
  };

  const getSubObjectives = (projectVersionId: string, subCategoryId: string) => {
    const activities = projectActivities[projectVersionId] || [];
    const subObjectives = activities.filter(act => act.subCategoryId === subCategoryId).reduce((acc, act) => {
      if (!acc.some(so => so.id === act.subObjectiveId)) {
        acc.push({ id: act.subObjectiveId, name: act.subObjectiveName, description: act.subObjectiveDescription, startDate: act.startDate, endDate: act.endDate });
      }
      return acc;
    }, [] as { id: string; name: string; description: string; startDate: string; endDate: string }[]);
    return subObjectives;
  };

  const getHistoryActivities = (projectVersionId: string, subObjectiveId: string) => {
    const activities = projectActivities[projectVersionId] || [];
    return activities.filter(act => act.subObjectiveId === subObjectiveId).map(act => ({
      id: act.id,
      name: act.activityName || act.name,
      startDate: act.startDate,
      endDate: act.endDate,
    }));
  };

  const handleClose = () => {
    const selectedItems: SelectedItem[] = [];
    selected.forEach(({ type, name }, id) => {
      selectedItems.push({ objectType: type as SearchObjectType, objectId: id, name });
    });
    onClose(selectedItems);
  };

  const historyDepthMap: Record<string, number> = {
    'History - Project': 1,
    'History - ProjectVersions': 2,
    'History - Category': 3,
    'History - SubCategory': 4,
    'History - Objective': 5,
    'History - Activity': 6,
  };

  const objectDepthMap: Record<string, number> = {
    'Object - Category': 1,
    'Object - SubCategory': 2,
    'Object - Activity': 3,
  };

  const isHistoryFilter = filterType.startsWith('History');
  const isObjectFilter = filterType.startsWith('Object');
  const historyMaxDepth = historyDepthMap[filterType] ?? 999;
  const objectMaxDepth = objectDepthMap[filterType] ?? 999;

  const showHistorySection = !filterType || isHistoryFilter;
  const showObjectSection = !filterType || isObjectFilter;

  const historyCheckboxAt = (depth: number) => !filterType || historyMaxDepth === depth;
  const objectCheckboxAt = (depth: number) => !filterType || objectMaxDepth === depth;
  const historyShowChildren = (depth: number) => depth < historyMaxDepth;
  const objectShowChildren = (depth: number) => depth < objectMaxDepth;

  return (
    <Modal open={open} onClose={handleClose} size="large">
    <BasicDialog
      hasSystemContext={false}
      showCancelButton={false}
      showSubmitButton={false}
      submitLabel=""
      submitColor={SemanticUIColor.PURPLE}
      className="no-padding"
    >
      <BasicDialogContainer hasSystemContext={false} title="Select Objects for AI Analysis" titleColor={SemanticUIColor.PURPLE} onClose={handleClose} contentHeight="auto" contentPadding={Size.SM} overflow={false}>
        <StyledContainer className="ai-object-picker">
        <StyledContainer className="ai-object-picker-toolbar">
          <Flexbox align={FlexAlign.START} justify={FlexJustify.START} className="ai-object-picker-toolbar-inner">
            <FlexItem noGrow className="ai-object-picker-filter">
              <Dropdown
                placeholder="Filter by type..."
                fluid
                selection
                clearable
                options={filterOptions}
                value={filterType}
                onChange={(e, { value }) => setFilterType(value as string)}
              />
            </FlexItem>
            <FlexItem className="ai-object-picker-chips">
              {Array.from(selected.entries()).map(([id, { name }]) => (
                <StyledContainer as="span" key={id} className="ai-object-picker-chip">
                  <span className="ai-object-picker-chip-label">{name}</span>
                  <Icon
                    name="close"
                    size="small"
                    className="ai-object-picker-chip-close"
                    onClick={() => {
                      const newSelected = new Map(selected);
                      newSelected.delete(id);
                      setSelected(newSelected);
                    }}
                  />
                </StyledContainer>
              ))}
            </FlexItem>
          </Flexbox>
        </StyledContainer>
        <StyledContainer className="ai-object-picker-tree">
        {loadingProjects ? (
          <Loader active inline="centered" />
        ) : (
          <List divided relaxed>
            {showHistorySection && (
            <List.Item>
              <List.Content>
                <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="ai-tree-row">
                  <Icon
                    name={expandedRoot ? 'chevron down' : 'chevron right'}
                    onClick={() => setExpandedRoot(!expandedRoot)}
                    className="ai-tree-toggle"
                  />
                  <List.Header>Projects</List.Header>
                </Flexbox>
                {expandedRoot && (
                  <List.List>
                    {projects.map(project => (
              <List.Item key={project.id}>
                <List.Content>
                  <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="ai-tree-row">
                    {historyCheckboxAt(1) && (
                    <Checkbox
                      checked={selected.has(project.id)}
                      onChange={() => toggleSelect(project.id, 'Project_Id', project.name)}
                      className="ai-tree-checkbox"
                    />
                    )}
                    {historyShowChildren(1) && (
                    <Icon
                      name={expandedProjects.has(project.id) ? 'chevron down' : 'chevron right'}
                      onClick={() => toggleExpand(project.id)}
                      className="ai-tree-toggle"
                    />
                    )}
                    <List.Header
                      title={`Launch Date: ${formatDate(project.launchDateUtc)}`}
                      className={historyShowChildren(1) ? 'ai-tree-header--clickable' : undefined}
                      onClick={() => historyShowChildren(1) && toggleExpand(project.id)}
                    >
                      {project.name}
                    </List.Header>
                  </Flexbox>
                  {historyShowChildren(1) && expandedProjects.has(project.id) && (
                    <List.List className="ai-tree-nested">
                      {loadingVersions.has(project.id) ? (
                        <Loader active inline size="small" />
                      ) : (
                        projectVersions[project.id]?.map(version => (
                          <List.Item key={version.id}>
                            <List.Content>
                              <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="ai-tree-row">
                                {historyCheckboxAt(2) && (
                                <Checkbox
                                  checked={selected.has(version.id)}
                                  onChange={() => toggleSelect(version.id, 'Project_Version_Id', version.name)}
                                  className="ai-tree-checkbox"
                                />
                                )}
                                {historyShowChildren(2) && (
                                <Icon
                                  name={expandedCategories.has(version.id) ? 'chevron down' : 'chevron right'}
                                  onClick={() => toggleExpandCategory(version.id)}
                                  className="ai-tree-toggle"
                                />
                                )}
                                <List.Header
                                  title={version.description}
                                  className={version.isActiveVersion ? 'ai-tree-header--active' : undefined}
                                >
                                  {version.name}
                                </List.Header>
                              </Flexbox>
                              {historyShowChildren(2) && expandedCategories.has(version.id) && (
                                <List.List className="ai-tree-nested">
                                  {getCategories(version.id).map(category => (
                                    <List.Item key={category.id}>
                                      <List.Content>
                                        <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="ai-tree-row">
                                          {historyCheckboxAt(3) && (
                                          <Checkbox
                                            checked={selected.has(category.id)}
                                            onChange={() => toggleSelect(category.id, 'Category_Id', category.name)}
                                            className="ai-tree-checkbox"
                                          />
                                          )}
                                          {historyShowChildren(3) && (
                                          <Icon
                                            name={expandedCategories.has(category.id) ? 'chevron down' : 'chevron right'}
                                            onClick={() => toggleExpandCategory(category.id)}
                                            className="ai-tree-toggle"
                                          />
                                          )}
                                          <List.Header title={category.description}>
                                            {category.name}
                                          </List.Header>
                                        </Flexbox>
                                        {historyShowChildren(3) && expandedCategories.has(category.id) && (
                                          <List.List className="ai-tree-nested">
                                            {getSubCategories(version.id, category.id).map(subCat => (
                                              <List.Item key={subCat.id}>
                                                <List.Content>
                                                  <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="ai-tree-row">
                                                    {historyCheckboxAt(4) && (
                                                    <Checkbox
                                                      checked={selected.has(subCat.id)}
                                                      onChange={() => toggleSelect(subCat.id, 'Sub_Category_Id', subCat.name)}
                                                      className="ai-tree-checkbox"
                                                    />
                                                    )}
                                                    {historyShowChildren(4) && (
                                                    <Icon
                                                      name={expandedSubCategories.has(subCat.id) ? 'chevron down' : 'chevron right'}
                                                      onClick={() => toggleExpandSubCategory(subCat.id)}
                                                      className="ai-tree-toggle"
                                                    />
                                                    )}
                                                    <List.Header title={subCat.description}>
                                                      {subCat.name}
                                                    </List.Header>
                                                  </Flexbox>
                                                  {historyShowChildren(4) && expandedSubCategories.has(subCat.id) && (
                                                    <List.List className="ai-tree-nested">
                                                      {getSubObjectives(version.id, subCat.id).map(subObj => (
                                                        <List.Item key={subObj.id}>
                                                          <List.Content>
                                                            <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="ai-tree-row">
                                                              {historyCheckboxAt(5) && (
                                                              <Checkbox
                                                                checked={selected.has(subObj.id)}
                                                                onChange={() => toggleSelect(subObj.id, 'Sub_Objective_Id', subObj.name)}
                                                                className="ai-tree-checkbox"
                                                              />
                                                              )}
                                                              {historyShowChildren(5) && (
                                                              <Icon
                                                                name={expandedSubObjectives.has(subObj.id) ? 'chevron down' : 'chevron right'}
                                                                onClick={() => toggleExpandSubObjective(subObj.id)}
                                                                className="ai-tree-toggle"
                                                              />
                                                              )}
                                                              <List.Header title={subObj.description}>
                                                                {subObj.name} ({formatDate(subObj.startDate)} - {formatDate(subObj.endDate)})
                                                              </List.Header>
                                                            </Flexbox>
                                                            {historyShowChildren(5) && expandedSubObjectives.has(subObj.id) && (
                                                              <List.List className="ai-tree-nested">
                                                                {getHistoryActivities(version.id, subObj.id).map(act => (
                                                                  <List.Item key={act.id}>
                                                                    <List.Content>
                                                                      <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="ai-tree-row">
                                                                        {historyCheckboxAt(6) && (
                                                                        <Checkbox
                                                                          checked={selected.has(act.id)}
                                                                          onChange={() => toggleSelect(act.id, 'Static_Project_Activity_Id', act.name)}
                                                                          className="ai-tree-checkbox"
                                                                        />
                                                                        )}
                                                                        <List.Header>
                                                                          {act.name} ({formatDate(act.startDate)} - {formatDate(act.endDate)})
                                                                        </List.Header>
                                                                      </Flexbox>
                                                                    </List.Content>
                                                                  </List.Item>
                                                                ))}
                                                              </List.List>
                                                            )}
                                                          </List.Content>
                                                        </List.Item>
                                                      ))}
                                                    </List.List>
                                                  )}
                                                </List.Content>
                                              </List.Item>
                                            ))}
                                          </List.List>
                                        )}
                                      </List.Content>
                                    </List.Item>
                                  ))}
                                </List.List>
                              )}
                            </List.Content>
                          </List.Item>
                        ))
                      )}
                    </List.List>
                  )}
                </List.Content>
              </List.Item>
            ))}
          </List.List>
        )}
      </List.Content>
    </List.Item>
            )}

            {showObjectSection && (
    <List.Item>
      <List.Content>
        <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="ai-tree-row">
          <Icon
            name={expandedObjectsRoot ? 'chevron down' : 'chevron right'}
            onClick={() => { setExpandedObjectsRoot(!expandedObjectsRoot); if (!objects || objects.length === 0) loadObjects(); }}
            className="ai-tree-toggle"
          />
          <List.Header>Objects</List.Header>
        </Flexbox>
        {expandedObjectsRoot && (
          <List.List className="ai-tree-nested">
            {loadingObjects ? (
              <Loader active inline size="small" />
            ) : (
              objects.map(cat => {
                const idField = (cat as any).id ?? (cat as any).Id;
                const display = (cat as any).displayName ?? (cat as any).DisplayName ?? '';
                const description = (cat as any).description ?? (cat as any).Description ?? '';
                return (
                  <List.Item key={idField}>
                    <List.Content>
                      <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="ai-tree-row">
                        {objectShowChildren(1) && (
                        <Icon
                          name={expandedObjectCategories.has(idField) ? 'chevron down' : 'chevron right'}
                          onClick={() => toggleExpandObjectCategory(idField)}
                          className="ai-tree-toggle"
                        />
                        )}
                        {objectCheckboxAt(1) && (
                        <Checkbox
                          checked={selected.has(idField)}
                          onChange={() => toggleSelect(idField, 'Category_Id', display)}
                          className="ai-tree-checkbox"
                        />
                        )}
                        <List.Header title={description}>
                          {display}
                        </List.Header>
                      </Flexbox>
                      {objectShowChildren(1) && expandedObjectCategories.has(idField) && (
                        <List.List className="ai-tree-nested">
                          {loadingObjectSubCategories.has(idField) ? (
                            <Loader active inline size="small" />
                          ) : (
                            (objectSubCategories[idField] || []).map(sub => {
                              const subId = (sub as any).id ?? (sub as any).Id;
                              const subDisplay = (sub as any).displayName ?? (sub as any).DisplayName ?? (sub as any).name ?? '';
                              const subDesc = (sub as any).description ?? (sub as any).Description ?? '';
                              return (
                                <List.Item key={subId}>
                                  <List.Content>
                                    <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="ai-tree-row">
                                      {objectShowChildren(2) && (
                                      <Icon
                                        name={expandedObjectSubCategories.has(subId) ? 'chevron down' : 'chevron right'}
                                        onClick={() => toggleExpandObjectSubCategory(subId)}
                                        className="ai-tree-toggle"
                                      />
                                      )}
                                      {objectCheckboxAt(2) && (
                                      <Checkbox
                                        checked={selected.has(subId)}
                                        onChange={() => toggleSelect(subId, 'Sub_Category_Id', subDisplay)}
                                        className="ai-tree-checkbox"
                                      />
                                      )}
                                      <List.Header title={subDesc}>
                                        {subDisplay}
                                      </List.Header>
                                    </Flexbox>
                                    {objectShowChildren(2) && expandedObjectSubCategories.has(subId) && (
                                      <List.List className="ai-tree-nested">
                                        {loadingObjectActivities.has(subId) ? (
                                          <Loader active inline size="small" />
                                        ) : (
                                          (objectActivities[subId] || []).sort((a: any, b: any) => {
                                            const aName = (a.displayName ?? a.DisplayName ?? '').toString();
                                            const bName = (b.displayName ?? b.DisplayName ?? '').toString();
                                            return aName.localeCompare(bName);
                                          }).map(act => {
                                            const actId = (act as any).id ?? (act as any).Id;
                                            const actDisplay = (act as any).displayName ?? (act as any).DisplayName ?? (act as any).name ?? '';
                                            const actDesc = (act as any).description ?? (act as any).Description ?? '';
                                            return (
                                              <List.Item key={actId}>
                                                <List.Content>
                                                  <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="ai-tree-row">
                                                    {objectCheckboxAt(3) && (
                                                    <Checkbox
                                                      checked={selected.has(actId)}
                                                      onChange={() => toggleSelect(actId, 'Activity_Id', actDisplay)}
                                                      className="ai-tree-checkbox"
                                                    />
                                                    )}
                                                    <List.Header title={actDesc}>
                                                      {actDisplay}
                                                    </List.Header>
                                                  </Flexbox>
                                                </List.Content>
                                              </List.Item>
                                            );
                                          })
                                        )}
                                      </List.List>
                                    )}
                                  </List.Content>
                                </List.Item>
                              );
                            })
                          )}
                        </List.List>
                      )}
                    </List.Content>
                  </List.Item>
                );
              })
            )}
          </List.List>
        )}
      </List.Content>
    </List.Item>
            )}
  </List>
        )}
      
        </StyledContainer>
        <StyledContainer textAlign={Align.RIGHT} padding={Size.SM}>
          <Button color="purple" onClick={handleClose}>Done</Button>
        </StyledContainer>
        </StyledContainer>
      </BasicDialogContainer>
    </BasicDialog>
    </Modal>
  );
};

export default AIObjectPicker;